import * as D from "@radix-ui/react-dialog";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";
import { X } from "@/icons";

// Modal dialog on Radix (focus trap, ESC, scroll-lock, ARIA for free).
// Overlay flex-centers Content so the zoom animation stays origin-clean.
export const Dialog = D.Root;
export const DialogTrigger = D.Trigger;
export const DialogClose = D.Close;

export function DialogContent({
  className,
  children,
  hideClose,
  ...props
}: ComponentPropsWithoutRef<typeof D.Content> & { hideClose?: boolean }) {
  const { t } = useTranslation();
  return (
    <D.Portal>
      <D.Overlay className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-overlay/60 p-4 backdrop-blur-sm data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out">
        <D.Content
          className={cn(
            "relative w-full max-w-md rounded-2xl border border-border bg-surface shadow-elevated focus:outline-none",
            "data-[state=open]:animate-zoom-in data-[state=closed]:animate-zoom-out",
            className,
          )}
          {...props}
        >
          {children}
          {!hideClose && (
            <D.Close
              aria-label={t("actions.close")}
              className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-lg text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/55"
            >
              <X className="h-4 w-4" />
            </D.Close>
          )}
        </D.Content>
      </D.Overlay>
    </D.Portal>
  );
}

export function DialogHeader({ className, ...p }: { className?: string; children?: ReactNode }) {
  return <div className={cn("flex flex-col gap-1 p-5 pb-3", className)} {...p} />;
}

export function DialogTitle({ className, ...p }: ComponentPropsWithoutRef<typeof D.Title>) {
  return <D.Title className={cn("font-display text-lg font-semibold", className)} {...p} />;
}

export function DialogDescription({ className, ...p }: ComponentPropsWithoutRef<typeof D.Description>) {
  return <D.Description className={cn("text-sm text-fg-muted", className)} {...p} />;
}

export function DialogBody({ className, ...p }: { className?: string; children?: ReactNode }) {
  return <div className={cn("px-5 py-1", className)} {...p} />;
}

export function DialogFooter({ className, ...p }: { className?: string; children?: ReactNode }) {
  return <div className={cn("flex items-center justify-end gap-2 p-5 pt-4", className)} {...p} />;
}
